import { TILE_DAYS, tileFirstDay, tileLastDay, tileOf } from "./timelineGeometry";

/**
 * What the server accepts in one request, kept in step with `PlannerRequestValidator`: a window is at most 400
 * days and a request at most 800 days in all, so seven tiles (392 days) per window and fourteen per request.
 */
export const LIMITS = {
  maxResourcesPerRequest: 100,
  maxWindowTiles: 7,
  maxWindowsPerRequest: 8,
  maxTilesPerRequest: 14,
  maxKnownRanges: 16,
};

/** The tiles that cover a closed range of days, limited to the timeline. */
export function tilesCovering(timeline, firstDay, lastDay) {
  const first = Math.max(0, tileOf(timeline, firstDay));
  const last = Math.min(timeline.tileCount - 1, tileOf(timeline, lastDay));
  const tiles = [];
  for (let tile = first; tile <= last; tile += 1) tiles.push(tile);
  return tiles;
}

/**
 * The tiles to hold for a viewport: those it shows plus a margin of at least one tile on each side, or a whole
 * viewport when that is wider, so ordinary scrolling finds its data already loaded.
 */
export function tilesToHold(timeline, firstDay, lastDay) {
  const margin = Math.max(TILE_DAYS, lastDay - firstDay + 1);
  return tilesCovering(timeline, firstDay - margin, lastDay + margin);
}

/** Merges sorted tile numbers into runs of consecutive tiles. */
export function toRuns(sortedTiles) {
  const runs = [];
  for (const tile of sortedTiles) {
    const last = runs[runs.length - 1];
    if (last && tile === last.end + 1) last.end = tile;
    else runs.push({ start: tile, end: tile });
  }
  return runs;
}

/** The closed range of days a run of tiles spans. */
export const runToDays = (timeline, run) => ({
  startDay: tileFirstDay(timeline, run.start),
  endDay: tileLastDay(timeline, run.end),
});

/** Distance in tiles from a tile to a run, 0 when the tile is inside it. */
const distanceToRun = (run, tile) => (tile < run.start ? run.start - tile : tile > run.end ? tile - run.end : 0);

/**
 * The covered tiles directly next to a run on each side, as runs, or nothing for a side that is not covered.
 * Whatever overlaps them is already held, so a request for the run never needs to bring it again.
 */
function coveredNeighbours(covered, run) {
  const neighbours = [];

  if (covered.has(run.start - 1)) {
    let start = run.start - 1;
    while (covered.has(start - 1)) start -= 1;
    neighbours.push({ start, end: run.start - 1 });
  }

  if (covered.has(run.end + 1)) {
    let end = run.end + 1;
    while (covered.has(end + 1)) end += 1;
    neighbours.push({ start: run.end + 1, end });
  }

  return neighbours;
}

const runKey = (run) => `${run.start}-${run.end}`;

/** Cuts a run into pieces of at most `maxTiles` tiles. */
function splitRun(run, maxTiles) {
  const pieces = [];
  for (let start = run.start; start <= run.end; start += maxTiles) {
    pieces.push({ start, end: Math.min(run.end, start + maxTiles - 1) });
  }
  return pieces;
}

/**
 * Plans the requests that bring the allocations of some resources for the tiles they are missing.
 *
 * Each resource is missing the tiles it should hold that are neither covered nor already being fetched.
 * Resources missing the same runs next to the same covered runs ask for the same thing, so they share requests,
 * and a request names the covered runs next to its windows so the server leaves out what is already held.
 * Requests respect the limits of the server and the nearest tiles to `centerTile` come first.
 *
 * @param timeline  the timeline the tiles belong to
 * @param resources  [{ id, covered: Set<tile>, inflight: Set<tile> }]
 * @param wanted  tiles every resource should hold, ascending
 * @param centerTile  the tile in the middle of the viewport
 * @returns [{ resourceIds, windows: [{ startDay, endDay }], knownRanges: [{ startDay, endDay }], tiles }]
 */
export function planCoverage(timeline, resources, wanted, centerTile, limits = LIMITS) {
  const groups = new Map();

  for (const resource of resources) {
    const missing = wanted.filter((tile) => !resource.covered.has(tile) && !resource.inflight.has(tile));
    if (missing.length === 0) continue;

    const runs = toRuns(missing);
    const neighbours = new Map(runs.map((run) => [runKey(run), coveredNeighbours(resource.covered, run)]));
    const signature = runs.map((run) => `${runKey(run)}|${neighbours.get(runKey(run)).map(runKey).join(",")}`).join(";");

    if (!groups.has(signature)) groups.set(signature, { runs, neighbours, resourceIds: [] });
    groups.get(signature).resourceIds.push(resource.id);
  }

  const requests = [];

  for (const { runs, neighbours, resourceIds } of groups.values()) {
    // Pieces of runs, nearest to the centre first, packed into requests within the limits.
    const pieces = runs
      .flatMap((run) => splitRun(run, limits.maxWindowTiles).map((piece) => ({ piece, run })))
      .sort((a, b) => distanceToRun(a.piece, centerTile) - distanceToRun(b.piece, centerTile));

    const batches = [];
    let current = null;

    for (const item of pieces) {
      const tileCount = item.piece.end - item.piece.start + 1;
      if (!current || current.windows.length >= limits.maxWindowsPerRequest || current.tileCount + tileCount > limits.maxTilesPerRequest) {
        current = { windows: [], runs: new Set(), tileCount: 0 };
        batches.push(current);
      }
      current.windows.push(item.piece);
      current.runs.add(item.run);
      current.tileCount += tileCount;
    }

    for (const batch of batches) {
      const known = new Map();
      for (const run of batch.runs) {
        for (const neighbour of neighbours.get(runKey(run))) known.set(runKey(neighbour), neighbour);
      }

      const tiles = batch.windows.flatMap((window) => tilesCovering(timeline, tileFirstDay(timeline, window.start), tileLastDay(timeline, window.end)));

      for (let offset = 0; offset < resourceIds.length; offset += limits.maxResourcesPerRequest) {
        requests.push({
          resourceIds: resourceIds.slice(offset, offset + limits.maxResourcesPerRequest),
          windows: batch.windows.map((window) => runToDays(timeline, window)),
          knownRanges: [...known.values()].slice(0, limits.maxKnownRanges).map((run) => runToDays(timeline, run)),
          tiles,
        });
      }
    }
  }

  const nearest = (request) => Math.min(...request.tiles.map((tile) => Math.abs(tile - centerTile)));
  return requests.sort((a, b) => nearest(a) - nearest(b));
}
